import { useContext, useMemo, useState } from "react";
import { Context } from "../../../context/Context";
import { useAccess } from "../../../access/hooks/useAccess";
import { Button, Checkbox, NoticeBox, Tags } from "../../ui";
import { setItemBranchVisibilityAPI } from "../../../utils/api/menu";
import { localized, menuErrorMessage } from "../menuRole";
import { menuActionsText } from "../menuActionsText";
import MenuDialog from "./MenuDialog";

// Per-branch visibility of one item, for the branches assigned to the menu
// (branch users only see their own). Ticked = visible. Saving sends one
// audited request per changed branch.
export default function ItemVisibilityDialog({ menu, item, branches, onClose, onSaved }) {
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const t = menuActionsText(lang);
  const hiddenNow = useMemo(() => new Set((item.visibility?.hiddenBranches || []).map((h) => String(h.branch))), [item]);
  const [visible, setVisible] = useState(() => new Set(branches.map((b) => String(b._id)).filter((id) => !hiddenNow.has(id))));
  const [error, setError] = useState("");

  const changes = branches.filter((b) => visible.has(String(b._id)) === hiddenNow.has(String(b._id)));
  const setAll = (on) => setVisible(new Set(on ? branches.map((b) => String(b._id)) : []));
  const toggle = (id) =>
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = async () => {
    setError("");
    let latest = null;
    const failed = [];
    for (const branch of changes) {
      // handler-level authorization, per branch (branchId = branch name)
      if (!can("menu.branch.visibility", { restaurantId: menu.restaurant?.id, branchId: branch.name })) {
        failed.push(`${branch.name}: ${menuErrorMessage(Object.assign(new Error("Not allowed"), { status: 403 }), lang)}`);
        continue;
      }
      try {
        latest = (await setItemBranchVisibilityAPI(menu._id, item._id, branch._id, visible.has(String(branch._id)))).menu;
      } catch (err) {
        failed.push(`${branch.name}: ${menuErrorMessage(err, lang)}`);
      }
    }
    if (latest) onSaved(latest, failed.length ? null : t.visibilitySaved);
    if (failed.length) setError(`${t.partialFailure(failed.length)} ${failed.join(" · ")}`);
    else onClose();
  };

  return (
    <MenuDialog title={`${t.visibilityTitle} — ${localized(item.name, lang)}`} onClose={onClose} onSubmit={branches.length ? submit : undefined} submitLabel={t.save} cancelLabel={branches.length ? t.cancel : t.close} submitDisabled={!changes.length} error={error}>
      <p className="menu-note">{t.visibilityHint}</p>
      {item.visibility?.hidden && (
        <NoticeBox variant="warning" size="sm">
          {t.hiddenEverywhereNote}
        </NoticeBox>
      )}
      {branches.length === 0 ? (
        <p className="menu-empty-state">{t.noAssignedBranches}</p>
      ) : (
        <>
          {branches.length > 1 && (
            <div className="menu-branches-tools">
              <Button type="button" variant="success" appearance="soft" size="xs" rounded onClick={() => setAll(true)}>
                {t.showSelected}
              </Button>
              <Button type="button" variant="warning" appearance="soft" size="xs" rounded onClick={() => setAll(false)}>
                {t.hideSelected}
              </Button>
            </div>
          )}
          <ul className="menu-branches-list menu-visibility-list">
            {branches.map((branch) => {
              const id = String(branch._id);
              const on = visible.has(id);
              return (
                <li key={id} className="menu-branch-row">
                  <Checkbox label={branch.name} checked={on} onChange={() => toggle(id)} />
                  <Tags variant={on ? "success" : "warning"} size="sm">
                    {on ? t.visibleIn : t.hiddenIn}
                  </Tags>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </MenuDialog>
  );
}
